import React from 'react'
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  useTheme,
  useMediaQuery,
  AppBar,
  Toolbar,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Divider
} from '@mui/material'
import {
  Menu as MenuIcon,
  Business as BusinessIcon,
  TrendingUp as TrendingUpIcon,
  Security as SecurityIcon,
  Analytics as AnalyticsIcon,
  Login as LoginIcon
} from '@mui/icons-material'
import { useAuth } from '../../auth/useAuth'

const PublicLandingPage: React.FC = () => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const { login } = useAuth()
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false)

  const handleLogin = async () => {
    try {
      await login()
    } catch (error) {
      console.error('Login failed:', error)
    }
  }

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen)
  }

  const navigationItems = [
    { label: 'Firm Overview', href: '#overview' },
    { label: 'Investment Philosophy', href: '#philosophy' },
    { label: 'Services', href: '#services' }
  ]

  const services = [
    {
      icon: <BusinessIcon sx={{ fontSize: 48, color: theme.palette.primary.main }} />,
      title: 'Fund Management',
      description: 'Professional fund management services with comprehensive portfolio oversight and strategic investment decisions.'
    },
    {
      icon: <TrendingUpIcon sx={{ fontSize: 48, color: theme.palette.primary.main }} />,
      title: 'Investment Strategy',
      description: 'Tailored investment strategies designed to meet specific risk profiles and return objectives.'
    },
    {
      icon: <SecurityIcon sx={{ fontSize: 48, color: theme.palette.primary.main }} />,
      title: 'Risk Management',
      description: 'Advanced risk assessment and management tools to protect and optimize investment portfolios.'
    },
    {
      icon: <AnalyticsIcon sx={{ fontSize: 48, color: theme.palette.primary.main }} />,
      title: 'Analytics & Reporting',
      description: 'Comprehensive analytics and detailed reporting for informed investment decision making.'
    }
  ]

  return (
    <Box sx={{ flexGrow: 1, minHeight: '100vh' }}>
      {/* Navigation Bar */}
      <AppBar position="static" sx={{ backgroundColor: '#1a237e' }}>
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1, fontWeight: 'bold' }}>
            Falcon Money Management
          </Typography>
          
          {isMobile ? (
            <IconButton
              size="large"
              edge="start"
              color="inherit"
              aria-label="menu"
              onClick={toggleMobileMenu}
            >
              <MenuIcon />
            </IconButton>
          ) : (
            <Box sx={{ display: 'flex', gap: 2 }}>
              {navigationItems.map((item) => (
                <Button
                  key={item.label}
                  color="inherit"
                  href={item.href}
                  sx={{ textTransform: 'none' }}
                >
                  {item.label}
                </Button>
              ))}
              <Button
                variant="contained"
                color="secondary"
                startIcon={<LoginIcon />}
                onClick={handleLogin}
                sx={{ ml: 2 }}
              >
                Login
              </Button>
            </Box>
          )}
        </Toolbar>
      </AppBar>

      {/* Mobile Menu Drawer */}
      <Drawer
        anchor="right"
        open={mobileMenuOpen}
        onClose={toggleMobileMenu}
      >
        <Box sx={{ width: 250 }} role="presentation">
          <List>
            {navigationItems.map((item) => (
              <ListItem key={item.label} component="a" href={item.href}>
                <ListItemText primary={item.label} />
              </ListItem>
            ))}
          </List>
        </Box>
      </Drawer>

      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #1a237e 0%, #3949ab 100%)',
          color: 'white',
          py: { xs: 8, md: 12 },
          textAlign: 'center'
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant={isMobile ? 'h3' : 'h2'}
            component="h1"
            gutterBottom
            sx={{ fontWeight: 'bold', mb: 3 }}
          >
            Professional Fund Management
          </Typography>
          <Typography
            variant={isMobile ? 'h6' : 'h5'}
            component="h2"
            sx={{ mb: 4, opacity: 0.9, maxWidth: '800px', mx: 'auto' }}
          >
            Authorised and regulated by the Financial Conduct Authority, 
            delivering sophisticated investment solutions with institutional-grade expertise.
          </Typography>
        </Container>
      </Box>

      {/* Services Section */}
      <Box id="services" sx={{ py: { xs: 6, md: 10 }, backgroundColor: '#f8f9fa' }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            component="h2"
            textAlign="center"
            gutterBottom
            sx={{ mb: 6, fontWeight: 'bold', color: '#1a237e' }}
          >
            Our Services
          </Typography>
          <Grid container spacing={4}>
            {services.map((service, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Card
                  sx={{
                    height: '100%',
                    textAlign: 'center',
                    p: 3,
                    transition: 'transform 0.3s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: theme.shadows[8]
                    }
                  }}
                >
                  <CardContent>
                    <Box sx={{ mb: 2 }}>
                      {service.icon}
                    </Box>
                    <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: 'bold' }}>
                      {service.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {service.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Overview Section */}
      <Box id="overview" sx={{ py: { xs: 6, md: 10 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography
                variant="h3"
                component="h2"
                gutterBottom
                sx={{ fontWeight: 'bold', color: '#1a237e' }}
              >
                Firm Overview
              </Typography>
              <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7 }}>
                Falcon Money Management LLP is a specialist investment management firm 
                authorised and regulated by the Financial Conduct Authority. We provide 
                institutional-grade investment solutions with a focus on delivering 
                consistent returns through disciplined risk management.
              </Typography>
              <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7 }}>
                Our team combines deep market expertise with cutting-edge technology 
                to deliver comprehensive fund management services tailored to our 
                clients' specific investment objectives and risk profiles.
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  backgroundColor: '#f8f9fa',
                  p: 4,
                  borderRadius: 2,
                  textAlign: 'center'
                }}
              >
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1a237e', mb: 2 }}>
                  FCA Authorised
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Regulated by the Financial Conduct Authority, ensuring the highest 
                  standards of professional conduct and client protection.
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Investment Philosophy Section */}
      <Box id="philosophy" sx={{ py: { xs: 6, md: 10 }, backgroundColor: '#f8f9fa' }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            component="h2"
            textAlign="center"
            gutterBottom
            sx={{ mb: 6, fontWeight: 'bold', color: '#1a237e' }}
          >
            Investment Philosophy
          </Typography>
          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Box textAlign="center">
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
                  Disciplined Approach
                </Typography>
                <Typography variant="body1">
                  We maintain a disciplined investment approach based on thorough 
                  research and rigorous risk assessment.
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box textAlign="center">
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
                  Long-term Focus
                </Typography>
                <Typography variant="body1">
                  Our investment strategies are designed with a long-term perspective 
                  to deliver sustainable returns.
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box textAlign="center">
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
                  Risk Management
                </Typography>
                <Typography variant="body1">
                  Comprehensive risk management is at the core of our investment 
                  decision-making process.
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>


      {/* Footer */}
      <Box
        component="footer"
        sx={{
          backgroundColor: '#1a237e',
          color: 'white',
          py: 4,
          textAlign: 'center'
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="body2" paragraph>
            Falcon Money Management LLP is authorised and regulated by the Financial Conduct Authority.
          </Typography>
          <Typography variant="body2">
            © 2025 Falcon Money Management LLP. All rights reserved.
          </Typography>
        </Container>
      </Box>
    </Box>
  )
}

export default PublicLandingPage
